import { InputNumber, Modal, Radio, Select, Table, Tag, Progress } from "antd";
import React, { useEffect, useState } from "react";
import { editProduct } from "../../../../../../redux/actions/productActions";
import { transformInToFormObject } from "../../../../../../utils/generateFormData";
import { connect } from "react-redux";

const RadioGroup = Radio.Group; 
const Option = Select.Option;


const BulkUpdateModal = ({ 
    open, 
    onCancel, 
    products, 
    updateProducts,
    editProduct,
    selectedRowKeys 
}) => {
    const [ updateValue, setUpdateValue ] = useState("");
    const [ percentage, setPercentage ] = useState(1);
    const [ inputPrice, setPrice ] = useState(0);
    const [ loading, setLoading ] = useState(false);
    const [ productToUpdate, setProductToUpdate ] = useState([]);
    const [ updateType, setUpdateType ] = useState("");
    const [ selectedProducts, setSelectedProducts ] = useState([]);
    const [ progress, setProgress ] = useState({ show: false, percent: 0, current: 0, total: 0 });

    const BATCH_SIZE = 5;
    const BATCH_DELAY_MS = 1500;

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        const productsObj = {};
        for (let i of products) {
            productsObj[i.id] = i;
        }

        const selected = selectedRowKeys
            && selectedRowKeys
            .map((id) => {
                const prod = productsObj[Number(id)];
                if (!prod) return null;
                return { ...prod };
            })
            .filter((prod) => prod);

        setSelectedProducts(selected || []);
    }, [products, selectedRowKeys]);

    const hasVariants = (product) => product.variants && product.variants.length > 0;

    const handleRadioChange = (e) => {
        setUpdateValue(e.target.value);
    }

    const handleUpdateTypeSelect = (e)=> {
        setUpdateType(e.target.value)
    }

    const calculatePercentageIncrease = (price, percentag) => {
        const percent = percentag / 100;
        const percentageValue = price * percent;
        const newPrice = price + percentageValue;
        return newPrice;
    }

    const calculatePercentageDecrease = (price, percentag) => {
        const percent = percentag / 100;
        const percentageValue = price * percent;
        const newPrice = price - percentageValue;
        return newPrice;
    }

    const calculateNewPrice = (oldPrice, method, type, value) => {
        const numPrice = Number(oldPrice);
        if (method === "price") {
            return type === "increase" 
                ? numPrice + value 
                : numPrice - value;
        }
        if (method === "percentage") {
            return type === "increase"
                ? calculatePercentageIncrease(numPrice, value)
                : calculatePercentageDecrease(numPrice, value);
        }
        return numPrice;
    }

    const handleEditBulkProduct = async () => {
        if(updateType === "") return Modal.warn({title: "Please select the update type"});
        if(selectedRowKeys.length === 0) return Modal.warn({title: "Please select the products to update"});
        if(updateValue === "") return Modal.warn({title: "Please select the method of update"});

        const updateAmount = updateValue === "price" ? inputPrice : percentage;

        if (updateAmount <= 0 || updateAmount === "" || updateAmount === null) {
            return Modal.warn({
                title: updateValue === "price" 
                    ? "Please enter a valid price" 
                    : "Please enter a valid percentage"
            });
        }

        setLoading(true);

        const requests = selectedProducts.map((product) => {
            const { id } = product;

            if (hasVariants(product)) {
                const updatedVariants = product.variants.map(variant => {
                    const newPrice = calculateNewPrice(
                        variant.price, updateValue, updateType, updateAmount
                    );
                    const obj = {};
                    for (let key in variant) {
                        if (variant[key] !== null && variant[key] !== undefined) {
                            obj[key] = variant[key];
                        }
                    }
                    obj.price = Math.max(0, Math.round(newPrice * 100) / 100);
                    return obj;
                });

                const mainPrice = calculateNewPrice(
                    product.price, updateValue, updateType, updateAmount
                );

                const payload = {
                    price: Math.max(0, Math.round(mainPrice * 100) / 100),
                    variants: updatedVariants
                };
                return { id, formData: transformInToFormObject(payload) };
            } else {
                const newPrice = calculateNewPrice(
                    product.price, updateValue, updateType, updateAmount
                );
                const payload = { 
                    price: Math.max(0, Math.round(newPrice * 100) / 100) 
                };
                return { id, formData: transformInToFormObject(payload) };
            }
        });

        const total = requests.length;
        setProgress({ show: true, percent: 0, current: 0, total });

        try {
            let completed = 0;

            for (let i = 0; i < total; i += BATCH_SIZE) {
                const batch = requests.slice(i, i + BATCH_SIZE);
                await Promise.all(batch.map(req => editProduct(req.id, req.formData)));
                completed += batch.length;
                const percent = Math.round((completed / total) * 100);
                setProgress({ show: true, percent, current: completed, total });
                // Yield to browser so the progress bar actually paints
                await new Promise(r => setTimeout(r, 50));
            }

            setLoading(false);
            setProgress({ show: false, percent: 100, current: total, total });
            Modal.success({
                title: "Bulk Price Update successful",
                onOk: () => {
                    updateProducts();
                    onCancel();
                }
            });
        } catch (err) {
            setLoading(false);
            setProgress({ show: false, percent: 0, current: 0, total: 0 });
            Modal.error({
                title: "An error occurred while updating prices",
                onOk: () => {
                    updateProducts();
                    onCancel();
                }
            });
        }
    }

    // Preview table columns
    const previewColumns = [
        {
            title: "Product",
            dataIndex: "name",
            render: (val, record) => (
                <span>
                    {val}
                    {hasVariants(record) && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                            {record.variants.length} variant{record.variants.length > 1 ? 's' : ''}
                        </Tag>
                    )}
                </span>
            ),
        },
        {
            title: "Current Price",
            dataIndex: "price",
            render: (val) => Number(val).toLocaleString(),
        },
        {
            title: "New Price",
            dataIndex: "price",
            render: (val) => {
                if (!updateValue || !updateType) return '-';
                const amount = updateValue === "price" ? inputPrice : percentage;
                if (!amount || amount <= 0) return '-';
                const newPrice = calculateNewPrice(val, updateValue, updateType, amount);
                return Math.max(0, Math.round(newPrice * 100) / 100).toLocaleString();
            },
        },
    ];

    // Preview variant sub-table columns
    const variantPreviewColumns = [
        {
            title: "Variant",
            render: (_, record) => `${record.type}: ${record.value}`,
        },
        {
            title: "Current Price",
            dataIndex: "price",
            render: (val) => val ? Number(val).toLocaleString() : '-',
        },
        {
            title: "New Price",
            dataIndex: "price",
            render: (val) => {
                if (!val || !updateValue || !updateType) return '-';
                const amount = updateValue === "price" ? inputPrice : percentage;
                if (!amount || amount <= 0) return '-';
                const newPrice = calculateNewPrice(val, updateValue, updateType, amount);
                return Math.max(0, Math.round(newPrice * 100) / 100).toLocaleString();
            },
        },
    ];

    const expandable = {
        expandedRowRender: (record) => {
            if (!hasVariants(record)) return null;
            const variantData = record.variants.map((v, idx) => ({
                ...v,
                key: `${record.id}-v-${idx}`,
            }));
            return (
                <Table
                    columns={variantPreviewColumns}
                    dataSource={variantData}
                    pagination={false}
                    size="small"
                    style={{ margin: '0' }}
                />
            );
        },
        rowExpandable: (record) => hasVariants(record),
    };

    const options = [
        {key: 1, value: "percentage", name: "Percentage"},
        {key: 2, value: "price", name: "Price"}
    ]
    const updateT = [
        {key: 1, value: "increase", name: "Increase product price"},
        {key: 2, value: "decrease", name: "Decrease product price"}
    ]

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            onOk={handleEditBulkProduct}
            okText="Edit Bulk Product"
            title="Bulk Price Update"
            okButtonProps={{loading: loading}}
            width={800}
            closable={!loading}
            maskClosable={!loading}
        >
            <div style={{marginBottom: "1rem"}} >
                <h3>Select update type</h3>
                <RadioGroup
                    defaultValue={""}
                    name="update Type"
                    onChange={(e)=>handleUpdateTypeSelect(e)}
                >
                    {updateT.map((upda)=>{
                        return <Radio key={upda.key} value={upda.value}>{upda.name}</Radio>
                    })}
                </RadioGroup>
            </div>

            <h3>Select Update Method</h3>
            <RadioGroup 
                defaultValue={""} 
                name="Select Increase Method" 
                onChange={(e)=>handleRadioChange(e)}
            >
                {options.map((val, index)=>{
                    return <Radio key={val.key} value={val.value}>{val.name}</Radio>
                })}
            </RadioGroup>
            
            {updateValue === "percentage" && 
                <div
                    style={{
                        display: "flex", 
                        alignItems: "center", 
                        gap: "15px",
                        marginTop: "15px"
                    }}
                >
                    <h4>Set percentage value</h4>
                    <InputNumber 
                        size="large"
                        placeholder="Set percentage value"
                        value={percentage}
                        onChange={(value)=>setPercentage(value)}
                    />
                </div>
            }
            {updateValue === "price" && 
                <div
                    style={{
                        display: "flex", 
                        alignItems: "center", 
                        gap: "15px",
                        marginTop: "15px"
                    }}
                >
                    <h4>Set price value</h4>
                    <InputNumber 
                        size="large"
                        placeholder="Set price value"
                        value={inputPrice}
                        onChange={(value)=>setPrice(value)}
                    />
                </div>
            }

            {/* Price preview table */}
            {selectedProducts.length > 0 && updateValue && updateType && (
                <div style={{ marginTop: 20 }}>
                    <h3>Price Preview</h3>
                    <p style={{ color: '#666', marginBottom: 8 }}>
                        Products with variants can be expanded to preview variant price changes.
                    </p>
                    <Table
                        columns={previewColumns}
                        dataSource={selectedProducts}
                        expandable={expandable}
                        rowKey="id"
                        pagination={false}
                        size="small"
                    />
                </div>
            )}
            {progress.show && (
                <div style={{ marginTop: 12, padding: '8px 0' }}>
                    <Progress 
                        percent={progress.percent} 
                        status="active" 
                        size="small"
                        format={() => `${progress.current}/${progress.total}`}
                    />
                </div>
            )}
        </Modal>
    )
}

const mapStateToProps = (state) => ({
    state: state
});
export default connect(mapStateToProps, {editProduct})(BulkUpdateModal);